/**
 * Constants for Expiring Contracts Feature
 * US-CONT-005: View Expiring Contracts Alert
 */

import type { ContractUrgency, ContractType, ContractSort } from '../types/expiringContracts.types';

// ============================================
// QUERY & PAGINATION DEFAULTS
// ============================================

/**
 * Default number of days to consider for expiring contracts
 */
export const DEFAULT_DAYS_THRESHOLD = 30;

/**
 * Default page size for pagination
 */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Maximum page size allowed
 */
export const MAX_PAGE_SIZE = 100;

/**
 * Minimum page size allowed
 */
export const MIN_PAGE_SIZE = 10;

// ============================================
// REACT QUERY CONFIGURATION
// ============================================

/**
 * Time to consider data fresh (5 minutes)
 */
export const STALE_TIME = 5 * 60 * 1000;

/**
 * Time to keep data in cache (10 minutes)
 */
export const CACHE_TIME = 10 * 60 * 1000;

/**
 * Refetch interval for dashboard widget (1 minute)
 */
export const REFETCH_INTERVAL = 60 * 1000;

// ============================================
// URGENCY THRESHOLDS
// ============================================

/**
 * Urgency level thresholds in days
 */
export const URGENCY_THRESHOLDS = {
  CRITICAL: 7, // 0-7 days
  WARNING: 14, // 8-14 days
  NORMAL: 30 // 15-30 days
} as const;

/**
 * Color schemes for urgency levels
 */
export const URGENCY_COLORS: Record<ContractUrgency, {
  badge: string;
  text: string;
  bg: string;
  border: string;
}> = {
  EXPIRED: {
    badge: 'destructive',
    text: 'text-destructive',
    bg: 'bg-destructive/10',
    border: 'border-destructive'
  },
  CRITICAL: {
    badge: 'destructive',
    text: 'text-destructive',
    bg: 'bg-destructive/10',
    border: 'border-destructive'
  },
  WARNING: {
    badge: 'default',
    text: 'text-yellow-700 dark:text-yellow-400',
    bg: 'bg-yellow-50 dark:bg-yellow-950',
    border: 'border-yellow-300 dark:border-yellow-700'
  },
  NORMAL: {
    badge: 'secondary',
    text: 'text-green-700 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-950',
    border: 'border-green-300 dark:border-green-700'
  }
};

/**
 * Labels for urgency levels
 */
export const URGENCY_LABELS: Record<ContractUrgency, string> = {
  EXPIRED: 'Expired',
  CRITICAL: 'Critical',
  WARNING: 'Warning',
  NORMAL: 'Normal'
};

// ============================================
// CONTRACT TYPE OPTIONS
// ============================================

/**
 * Contract type labels for display
 */
export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  SUPPLY: 'Supply Contract',
  SERVICE: 'Service Contract',
  SALES: 'Sales Contract',
  MAINTENANCE: 'Maintenance Contract',
  OTHER: 'Other'
};

/**
 * Contract type options for filters
 */
export const CONTRACT_TYPE_OPTIONS = [
  { value: 'SUPPLY', label: 'Supply Contract' },
  { value: 'SERVICE', label: 'Service Contract' },
  { value: 'SALES', label: 'Sales Contract' },
  { value: 'MAINTENANCE', label: 'Maintenance Contract' },
  { value: 'OTHER', label: 'Other' }
] as const;

// ============================================
// URGENCY FILTER OPTIONS
// ============================================

/**
 * Urgency filter options
 */
export const URGENCY_FILTER_OPTIONS = [
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CRITICAL', label: 'Critical (0-7 days)' },
  { value: 'WARNING', label: 'Warning (8-14 days)' },
  { value: 'NORMAL', label: 'Normal (15-30 days)' }
] as const;

// ============================================
// SORT OPTIONS
// ============================================

/**
 * Sort field options
 */
export const SORT_FIELD_OPTIONS = [
  { value: 'endDate', label: 'Expiry Date' },
  { value: 'contractNumber', label: 'Contract Number' },
  { value: 'customerName', label: 'Customer Name' },
  { value: 'contractValue', label: 'Contract Value' },
  { value: 'createdAt', label: 'Created Date' }
] as const;

/**
 * Default sort configuration
 */
export const DEFAULT_SORT: ContractSort = {
  field: 'endDate',
  direction: 'asc'
};

// ============================================
// STATUS OPTIONS
// ============================================

/**
 * Contract status filter options
 */
export const STATUS_FILTER_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'EXPIRING_SOON', label: 'Expiring Soon' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'RENEWED', label: 'Renewed' },
  { value: 'TERMINATED', label: 'Terminated' }
] as const;

// ============================================
// ACTION LABELS
// ============================================

/**
 * Contract action labels for display
 */
export const ACTION_LABELS = {
  RENEW: 'Renew Contract',
  VIEW_DETAILS: 'View Details',
  CONTACT_CUSTOMER: 'Contact Customer',
  DOWNLOAD: 'Download Contract',
  EDIT: 'Edit Contract',
  TERMINATE: 'Terminate Contract'
} as const;

// ============================================
// DATE FORMATS
// ============================================

/**
 * Date format strings
 */
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy', // Jan 15, 2025
  DISPLAY_WITH_TIME: 'MMM dd, yyyy HH:mm', // Jan 15, 2025 14:30
  ISO: 'yyyy-MM-dd', // 2025-01-15
  FULL: 'MMMM dd, yyyy', // January 15, 2025
  SHORT: 'MM/dd/yyyy' // 01/15/2025
} as const;

// ============================================
// VALIDATION
// ============================================

/**
 * Validation constraints
 */
export const VALIDATION = {
  MIN_CONTRACT_VALUE: 0,
  MAX_CONTRACT_VALUE: 999999999,
  MIN_SEARCH_LENGTH: 2,
  MAX_SEARCH_LENGTH: 100
} as const;

// ============================================
// ERROR MESSAGES
// ============================================

/**
 * Error messages
 */
export const ERROR_MESSAGES = {
  FETCH_FAILED: 'Failed to load expiring contracts',
  RENEW_FAILED: 'Failed to renew contract',
  DOWNLOAD_FAILED: 'Failed to download contract',
  NO_DATA: 'No expiring contracts found',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action',
  SERVER_ERROR: 'Server error. Please try again later.'
} as const;

// ============================================
// SUCCESS MESSAGES
// ============================================

/**
 * Success messages
 */
export const SUCCESS_MESSAGES = {
  RENEW_SUCCESS: 'Contract renewed successfully',
  DOWNLOAD_SUCCESS: 'Contract downloaded successfully',
  UPDATE_SUCCESS: 'Contract updated successfully'
} as const;
